import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiMail } from "react-icons/fi";
import ApiEndPoints from "../../ServicesCalls/ApiEndPoints";
import { FaEye, FaEyeSlash, FaSpinner } from "react-icons/fa";
import loginBackground from "../../Assets/login-background_image.png";

function Login({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [message, setMessage] = useState({ text: "", color: "" });
  const [formState, setFormState] = useState({
    email: "",
    password: "",
    countdown: 60,
    resetEmail: "",
    mailSent: false,
    rememberMe: false,
    isLoggingIn: false,
    showPassword: false,
    isSendingEmail: false,
    mailSentTimestamp: null,
    showForgotPassword: false,
  });

  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const rememberedPassword = localStorage.getItem("rememberedPassword");
    if (rememberedEmail && rememberedPassword) {
      setFormState((prev) => ({
        ...prev,
        rememberMe: true,
        email: rememberedEmail,
        resetEmail: rememberedEmail,
        password: rememberedPassword,
      }));
    }
  }, []);

  useEffect(() => {
    let interval;
    if (formState.mailSent && formState.mailSentTimestamp) {
      interval = setInterval(() => {
        const elapsed = Math.floor(
          (Date.now() - formState.mailSentTimestamp) / 1000
        );
        const remaining = 60 - elapsed;
        if (remaining <= 0) {
          clearInterval(interval);
          setFormState((prev) => ({ ...prev, countdown: 0 }));
        } else {
          setFormState((prev) => ({ ...prev, countdown: remaining }));
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [formState.mailSent, formState.mailSentTimestamp]);

  // Helper to update remembered credentials in localStorage
  const updateRememberedCredentials = (remember, email, password) => {
    if (remember) {
      localStorage.setItem("rememberedEmail", email);
      localStorage.setItem("rememberedPassword", password);
    } else {
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("rememberedPassword");
    }
  };

  // Helper to reset forgot password modal state
  const resetForgotPasswordState = () => {
    setFormState((prev) => ({
      ...prev,
      countdown: 60,
      resetEmail: "",
      mailSent: false,
      newPassword: "",
      confirmPassword: "",
      mailSentTimestamp: null,
      showForgotPassword: false,
    }));
    setMessage({ text: "", color: "" });
  };

  const fetchLogin = async () => {
    try {
      const requestHeaders = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Email: formState.email,
          Password: formState.password,
        }),
      };
      const response = await fetch(ApiEndPoints.postLoginUrl(), requestHeaders);
      const data = await response.json();
      const dataMsg = data?.message;

      if (!response.ok) throw new Error(dataMsg || "Failed to Fetch Login API");

      const userRole = data?.Role;
      const authToken = data?.token;
      const userLocations = data?.Location;

      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("loginTimestamp", Date.now().toString());
      const locationValue =
        (Array.isArray(userLocations) ? userLocations : [userLocations]) || [];
      localStorage.setItem("userRole", userRole);
      localStorage.setItem("apiAuthToken", authToken);
      localStorage.setItem("userLocation", JSON.stringify(locationValue));
      setIsAuthenticated(true);
      setMessage({ text: dataMsg, color: "green" });
      updateRememberedCredentials(
        formState.rememberMe,
        formState.email,
        formState.password
      );
      navigate(location.state?.from?.pathname || "/dashboard", {
        replace: true,
      });
    } catch (err) {
      const errorMsg = err.message;
      console.error(errorMsg);
      setIsAuthenticated(false);
      setMessage({ text: errorMsg, color: "red" });
      localStorage.setItem("isAuthenticated", "false");
    } finally {
      setFormState((prev) => ({ ...prev, isLoggingIn: false }));
    }
  };

  const fetchForgotPassword = async () => {
    try {
      const requestHeaders = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
        },
        body: JSON.stringify({ email: formState.resetEmail }),
      };

      const response = await fetch(
        ApiEndPoints.poastLoginForgotPasswordMail(),
        requestHeaders
      );
      const data = await response.json();
      const dataSuccess = data?.message;
      const dataError = data?.error;

      if (!response.ok)
        throw new Error(
          dataSuccess || dataError || "Failed to Fetch Mail Verification API"
        );

      setMessage({ text: dataSuccess, color: "green" });
      setFormState((prev) => ({
        ...prev,
        countdown: 60,
        mailSent: true,
        mailSentTimestamp: Date.now(),
      }));
    } catch (err) {
      const errorMsg = err.message;
      console.error(errorMsg);
      setMessage({ text: errorMsg, color: "red" });
    } finally {
      setFormState((prev) => ({ ...prev, isSendingEmail: false }));
    }
  };

  const handleSignInForm = (e) => {
    e.preventDefault();
    setMessage({ text: "", color: "" });
    setFormState((prev) => ({ ...prev, isLoggingIn: true }));
    fetchLogin();
  };

  const handleChange = (field) => (e) => {
    const value = field === "rememberMe" ? e.target.checked : e.target.value;
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (field === "rememberMe") {
      updateRememberedCredentials(
        e.target.checked,
        formState.email,
        formState.password
      );
    }
    if (field === "email" || field === "password") {
      setMessage({ text: "", color: "" });
    }
  };

  const handleEyeSymbol = () => {
    setFormState((prev) => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  // Email validation regex for forgot password
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    formState.email.trim()
  );

  const handleForgotPassword = () => {
    if (!isValidEmail) return;
    setFormState((prev) => ({
      ...prev,
      resetEmail: prev.email,
      showForgotPassword: true,
    }));
    setMessage({ text: "", color: "" });
  };

  const handleCloseorCancel = () => {
    resetForgotPasswordState();
  };

  const handleForgotPasswordForm = (e) => {
    e.preventDefault();
    setMessage({ text: "", color: "" });
    if (!formState.resetEmail) {
      setMessage({ text: "Email is required.", color: "red" });
      return;
    }
    setFormState((prev) => ({ ...prev, isSendingEmail: true }));
    fetchForgotPassword();
  };

  return (
    <>
      <div
        className="flex items-center justify-center min-h-screen px-4 py-12 sm:px-6 lg:px-8"
        style={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundImage: `url(${loginBackground})`,
        }}
      >
        <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md">
          <div>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-left text-gray-900">
              Sign in
            </h2>
            <p className="pt-3 text-gray-600 text-md">
              Welcome to a new experience of...
            </p>
          </div>

          {formState.isLoggingIn && (
            <div
              className="px-4 py-3 mb-4 text-blue-700 bg-blue-100 border border-blue-400 rounded"
              role="status"
            >
              Logging in, please wait...
            </div>
          )}

          {message.text && (
            <p
              className={`text-sm text-center ${
                message.color === "green"
                  ? "text-green-600"
                  : message.color === "red"
                  ? "text-red-600"
                  : ""
              }`}
            >
              {message.text}
            </p>
          )}

          <form onSubmit={handleSignInForm}>
            <div className="space-y-px">
              <div className="flex items-center gap-1 mb-4">
                <label
                  htmlFor="email-address"
                  className="w-32 font-bold text-md"
                >
                  Email Address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="flex-1 px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded appearance-none focus:z-10 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                  placeholder="Email your email"
                  value={formState.email}
                  onChange={handleChange("email")}
                />
              </div>
              <div className="flex items-center gap-1 mb-0">
                <label htmlFor="password" className="w-32 font-bold text-md">
                  Password
                </label>
                <div className="relative flex-1">
                  <input
                    id="password"
                    name="password"
                    type={formState.showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="w-full px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 border border-gray-300 rounded appearance-none focus:z-10 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    placeholder="Enter your password"
                    value={formState.password}
                    onChange={handleChange("password")}
                  />
                  <span
                    onClick={handleEyeSymbol}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 cursor-pointer"
                  >
                    {formState.showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 my-3 text-sm text-gray-600">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={formState.rememberMe}
                  onChange={handleChange("rememberMe")}
                />
                <span className="ml-1 font-semibold text-black text-md">
                  Remember me
                </span>
              </label>
              <span
                className={`relative ${
                  isValidEmail
                    ? "text-blue-600 cursor-pointer hover:underline"
                    : "text-gray-400 cursor-not-allowed"
                }`}
                onClick={handleForgotPassword}
              >
                Forgot password?
              </span>
            </div>

            <div>
              <button
                type="submit"
                disabled={
                  formState.isLoggingIn ||
                  !formState.email.trim() ||
                  !formState.password.trim()
                }
                className={`relative flex justify-center w-full px-4 py-2 mb-2 text-sm font-medium text-white border border-transparent rounded-md group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  formState.isLoggingIn ||
                  !formState.email.trim() ||
                  !formState.password.trim()
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
      {formState.showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-md p-6 bg-white rounded shadow-md">
            {message.text && (
              <p
                className={`mt-4 text-sm text-center ${
                  message.color === "green"
                    ? "text-green-600"
                    : message.color === "red"
                    ? "text-red-600"
                    : ""
                }`}
              >
                {message.text}
              </p>
            )}
            {formState.isSendingEmail && (
              <div className="flex items-center justify-center gap-2 mt-4 text-gray-700">
                <FaSpinner
                  size={20}
                  className="w-6 h-6 text-blue-500 animate-spin"
                />
                <span>Please wait...</span>
              </div>
            )}
            <form
              onSubmit={handleForgotPasswordForm}
              className="w-full max-w-md p-8 space-y-6 text-center bg-white rounded-lg shadow-md"
            >
              {!formState.mailSent ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Forgot Your Password?
                  </h2>
                  <p className="text-gray-600">
                    Enter your email address and we will send you a link to
                    reset your password.
                  </p>
                  <input
                    type="email"
                    placeholder="Email address*"
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    value={formState.resetEmail}
                    onChange={handleChange("resetEmail")}
                    required
                  />
                  <button
                    disabled={formState.isSendingEmail}
                    type="submit"
                    className={`w-full px-4 py-2 mt-4 font-medium text-white rounded ${
                      formState.isSendingEmail
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-700 hover:bg-blue-800"
                    }`}
                  >
                    {formState.isSendingEmail
                      ? "Sending, Email..."
                      : "Send Email"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseorCancel}
                    className="text-sm text-blue-700 underline"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleCloseorCancel}
                    className="absolute top-0 text-2xl font-bold text-gray-500 right-3 hover:text-gray-800"
                  >
                    ×
                  </button>
                  <div className="flex justify-center">
                    <FiMail className="w-16 h-16 mx-auto text-green-700" />
                  </div>
                  <h2 className="text-xl font-semibold text-center text-gray-800">
                    Please, Check Your Email!
                  </h2>
                  <button
                    type="button"
                    disabled={formState.countdown > 0}
                    onClick={handleForgotPasswordForm}
                    className={`w-full px-4 py-2 mt-4 font-medium text-white rounded ${
                      formState.countdown > 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-700 hover:bg-blue-800"
                    }`}
                  >
                    {formState.countdown > 0
                      ? `Resend Email in ${formState.countdown}s`
                      : "Resend Email"}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Login;
